import { ClientEvents, MealPlanAnalyticsLocation } from 'common/events/ClientEvents';
import React, { useEffect, useRef, useState } from 'react';
import { useFeatureFlag } from 'src/feature-flags/consumers';
import { MealPlanShefFragment } from 'src/gqlReactTypings.generated.d';
import { MealPlanFoodItem } from 'src/pages/consumer/meal-plans/checkout/types';
import {
  IMAGE_HEIGHT_CAROUSEL,
  IMAGE_WIDTH_CAROUSEL,
} from 'src/pages/consumer/meal-plans/components/MealPlanDishCard/consts';
import { MealPlanDishImage } from 'src/pages/consumer/meal-plans/components/MealPlanDishCard/MealPlanDishImage';
import { useTracker } from 'src/shared/hooks/useTracker';
import { CloseButton } from 'web-common/src/shared/design-system/components/Button/CloseButton';
import { Modal } from 'web-common/src/shared/design-system/components/Modal';
import { ModalType } from 'web-common/src/shared/design-system/components/Modal/types';
import { DishModalContent } from './DishModalContent';
import { DishModalFooter } from './DishModalFooter';
import { DishModalHeader } from './DishModalHeader';
import { DishModalInfo } from './DishModalInfo';
import { DishModalNav } from './DishModalNav';
import { DishModalReviews } from './DishModalReviews';
import { DishModalTitle } from './DishModalTitle';
import { IngredientsSection } from './IngredientsSection';
import { InteractionButtons } from './InteractionButtons';
import { NutritionMacros } from './NutritionMacros';
import { ShefBioSection } from './ShefBioSection';

interface DishModalRevampProps {
  bottomLeftChip?: React.ReactNode;
  isShowing?: boolean;
  foodItem: MealPlanFoodItem;
  shef: MealPlanShefFragment;
  unitsInCart: number;
  handleClose: () => void;
  servingsPerMeal?: number;
  showAddButton?: boolean;
  productType?: 'marketplace' | 'subscriptions';
  location?: string;
  analyticsLocation?: MealPlanAnalyticsLocation;
  collectionId?: string;
  // Footer props (only needed when showAddButton is true)
  quantity?: number;
  onQuantityChange?: (quantity: number) => void;
  minQuantity?: number | undefined;
  maxQuantity?: number | undefined;
  isServingSizeSelected?: boolean;
  isEditingCart?: boolean;
  hasBeenUpdated?: boolean;
  actionButtonClick?: () => void;
  priceToDisplay?: number | null;
}

export const DishModalRevamp: React.FC<DishModalRevampProps> = ({
  bottomLeftChip,
  isShowing = false,
  foodItem,
  shef,
  unitsInCart,
  handleClose,
  servingsPerMeal,
  showAddButton = true,
  productType = 'marketplace',
  location = null,
  analyticsLocation = 'dish-modal',
  collectionId,
  quantity = 1,
  onQuantityChange,
  minQuantity,
  maxQuantity,
  isServingSizeSelected = true,
  isEditingCart = false,
  hasBeenUpdated = false,
  actionButtonClick,
  priceToDisplay,
}) => {
  const tracker = useTracker();
  const { spiceLevelCustomizations, servingSizeCustomizations } = foodItem;
  const foodItemMacrosEnabled = useFeatureFlag('foodItemMacrosEnabled');
  const shouldRenderMacros = foodItemMacrosEnabled && foodItem.macros;

  const closeButtonRef = useRef<HTMLDivElement>(null);
  const modalContainerRef = useRef<HTMLDivElement>(null);
  const dishInfoRef = useRef<HTMLDivElement>(null);
  const ingredientsRef = useRef<HTMLDivElement>(null);
  const nutritionRef = useRef<HTMLDivElement>(null);
  const reviewsRef = useRef<HTMLDivElement>(null);
  const shefBioRef = useRef<HTMLDivElement>(null);
  const [dishNameRef, setDishNameRef] = useState<HTMLParagraphElement | null>(null);
  const [headerRef, setHeaderRef] = useState<HTMLDivElement | null>(null);

  const renderCloseButton = (
    <div ref={closeButtonRef}>
      <CloseButton className='absolute right-2 top-2 z-10 rounded-lg bg-white p-2.5 shadow-md' onClick={handleClose} />
    </div>
  );

  const renderDishImage = (
    <MealPlanDishImage
      imageUrl={foodItem.imageUrl ?? ''}
      height={IMAGE_HEIGHT_CAROUSEL}
      width={IMAGE_WIDTH_CAROUSEL}
      remainingAvailableUnits={Infinity} // TODO fix
      unitsInCart={unitsInCart}
      upsell={foodItem.upsell}
      ingredientsFlagged={foodItem.ingredientsFlagged}
      hideIngredientsFlaggedChip
      merchandisingTags={foodItem.merchandisingTags}
      bottomLeftChip={bottomLeftChip}
    />
  );

  useEffect(() => {
    if (isShowing) {
      tracker.track(ClientEvents.DISH_MODAL_VISIT, {
        foodItemId: foodItem.id,
        type: 'customizations',
        productType,
        dishServingSizeOptionsCount: servingSizeCustomizations?.length ?? 0,
        dishSpiceLevelOptionsCount: spiceLevelCustomizations?.length ?? 0,
        location,
        collectionId,
        cuisines:
          'cuisineCategories' in foodItem ? foodItem.cuisineCategories.map((c) => c.title).join(', ') : undefined,
      });
    }
    // Intentionally only adding isShowing to dependencies, since this is for resetting the modal
  }, [isShowing]);

  return (
    <Modal isShowing={isShowing} onClose={handleClose} modalType={ModalType.FULL_SCREEN_MOBILE}>
      <div
        ref={modalContainerRef}
        className='flex max-h-svh w-full flex-col overflow-y-auto overflow-x-hidden
          overscroll-contain bg-white md:h-[calc(100dvh-64px)] md:max-h-[calc(100dvh-64px)] md:rounded-lg'>
        {renderCloseButton}
        <DishModalHeader
          foodItem={foodItem}
          onClose={handleClose}
          closeButtonRef={closeButtonRef}
          onHeaderRef={setHeaderRef}
        />
        <DishModalNav
          dishInfoRef={dishInfoRef.current}
          ingredientsRef={ingredientsRef.current}
          nutritionRef={nutritionRef.current}
          reviewsRef={reviewsRef.current}
          shefBioRef={shefBioRef.current}
          modalContainerRef={modalContainerRef}
          dishNameRef={dishNameRef}
          headerRef={headerRef}
        />
        {renderDishImage}

        <DishModalContent>
          <DishModalTitle foodItem={foodItem} dishNameRef={setDishNameRef} />

          <div ref={dishInfoRef}>
            <DishModalInfo foodItem={foodItem} servingsPerMeal={servingsPerMeal} />
          </div>
          <div ref={ingredientsRef}>
            <IngredientsSection
              ingredients={foodItem.ingredients}
              numIngredientsFlagged={foodItem.ingredientsFlagged?.length ?? 0}
            />
          </div>
        </DishModalContent>

        {shouldRenderMacros && foodItem.macros && (
          <DishModalContent ref={nutritionRef} className='bg-sourdough py-6'>
            <NutritionMacros
              calories={foodItem.macros.calories}
              protein={foodItem.macros.protein}
              totalCarbohydrate={foodItem.macros.totalCarbohydrate}
              totalFat={foodItem.macros.totalFat}
              sodium={foodItem.macros.sodium}
            />
          </DishModalContent>
        )}

        {/* Interaction buttons */}
        <InteractionButtons foodItem={foodItem} analyticsLocation={analyticsLocation} />

        <DishModalContent ref={reviewsRef} className='bg-rice pb-8 pt-6'>
          <DishModalReviews
            foodItemId={foodItem.id}
            averageRating={foodItem.averageRating}
            ratingCount={foodItem.ratingCount}
            topPositiveTags={foodItem.topPositiveTags}
          />
        </DishModalContent>

        <ShefBioSection
          ref={shefBioRef}
          shefId={shef.id}
          bio={shef.shefProfile?.bio}
          imageUrl={shef.imageUrl}
          publicName={shef.publicName}
          isCartEnabled={showAddButton}
          analyticsLocation={analyticsLocation}
        />

        {showAddButton && onQuantityChange && (
          <DishModalFooter
            quantity={quantity}
            onQuantityChange={onQuantityChange}
            minQuantity={minQuantity}
            maxQuantity={maxQuantity}
            isServingSizeSelected={isServingSizeSelected}
            isEditingCart={isEditingCart}
            hasBeenUpdated={hasBeenUpdated}
            actionButtonClick={actionButtonClick}
            priceToDisplay={priceToDisplay}
          />
        )}
      </div>
    </Modal>
  );
};
